import { TARGET_FIELDS } from "components/form/fields/TiVoTargets";
import {
  APP_CATEGORIES,
  APP_TYPE,
  COMMERCIAL_AGREEMENT_TYPE,
  CONTRACT_ORIGIN,
  CONTRACT_STATUS,
  PARTNER_PICKS_REPORTINGS,
  PARTNER_PICKS_TYPES,
  RECOMMENDATION_TYPES,
  SUPPORT_PRIORITY,
  YES_NO_UNKNOWN,
} from "hooks/tivoApps";
import { trans } from "src/translations";
import { FieldProps } from "src/types/form";
import { toFieldValues } from "utils/formik";
import { FieldsConfig } from "utils/validation";

import {
  ACR_RIGHTS_LABELS,
  CSP_BIZ_MODEL_LABELS,
  CSP_GROUP_LABELS,
  IMAGE_LABELS,
  IMAGES,
  LAUNCH_TYPE_LABELS,
  PRESENTATION_ATTRIBUTES_LABELS,
  PRESENTATION_BOOLEAN,
  IMAGE_ENVS,
} from "../constants";

const PROVIDER_ID_PATTERN = /^[A-Za-z0-9_:".-]+$/;

export const FIELDS_PRESENTATION_CONFIG: Record<string, FieldProps> = {
  ALWAYS_VISIBLE_IN_MY_APPS: {
    label: PRESENTATION_ATTRIBUTES_LABELS.alwaysVisibleInMyApps,
    name: "attributes.alwaysVisibleInMyApps",
    type: "select",
    values: toFieldValues(PRESENTATION_BOOLEAN),
  },
  APP_MANIFEST_AVAILABLE: {
    label: PRESENTATION_ATTRIBUTES_LABELS.appManifestAvailable,
    name: "attributes.appManifestAvailable",
    type: "select",
    values: toFieldValues(PRESENTATION_BOOLEAN),
  },
  BROWSE_PLAYBACK_SORT_ORDER: {
    label: PRESENTATION_ATTRIBUTES_LABELS.browsePlaybackSortOrder,
    name: "attributes.browsePlaybackSortOrder",
    required: false,
    type: "number",
  },
  COUNTRIES: {
    label: "Countries",
    name: "countries",
    type: "array",
  },
  ENABLED_BY_DEFAULT: {
    label: PRESENTATION_ATTRIBUTES_LABELS.enabledByDefault,
    name: "attributes.enabledByDefault",
    type: "select",
    values: toFieldValues(PRESENTATION_BOOLEAN),
  },
  IN_MY_APPS_BY_DEFAULT: {
    label: PRESENTATION_ATTRIBUTES_LABELS.inMyAppsByDefault,
    name: "attributes.inMyAppsByDefault",
    type: "select",
    values: toFieldValues(PRESENTATION_BOOLEAN),
  },
  MATCH_BY_PLATFORM_SUPPORTED_FEATURES: {
    label: "Match by platform supported features",
    name: "matchByPlatformSupportedFeatures",
    type: "switch",
  },
  MY_APPS_RANGE_GROUP: {
    label: PRESENTATION_ATTRIBUTES_LABELS.myAppsRangeGroup,
    name: "attributes.myAppsRangeGroup",
    required: false,
    type: "number",
  },
  NAME: {
    label: PRESENTATION_ATTRIBUTES_LABELS.name,
    name: "attributes.name",
    required: false,
  },
  PLATFORM_SUPPORTED_APPS: {
    label: PRESENTATION_ATTRIBUTES_LABELS.platformSupportedApps,
    name: "attributes.platformSupportedApps",
    required: false,
    type: "array",
  },
  PLATFORM_SUPPORTED_FEATURES: {
    label: "Platform supported features",
    name: "platformSupportedFeatures",
    required: false,
    type: "array",
  },
  PROMOTED: {
    label: PRESENTATION_ATTRIBUTES_LABELS.promoted,
    name: "attributes.promoted",
    type: "select",
    values: toFieldValues(PRESENTATION_BOOLEAN),
  },
  PUBLISH_DATE: {
    label: PRESENTATION_ATTRIBUTES_LABELS.publishDateTime,
    name: "attributes.publishDateTime",
    required: false,
    type: "date",
  },
  POST_PUBLISH_DAYS_CONSIDERED_NEW: {
    label: PRESENTATION_ATTRIBUTES_LABELS.postPublishDaysConsideredNew,
    name: "attributes.postPublishDaysConsideredNew",
    required: false,
    type: "number",
    min: 0,
  },
  RECOMMENDATION_TYPE: {
    label: PRESENTATION_ATTRIBUTES_LABELS.recommendationsType,
    name: "attributes.recommendationsType",
    type: "select",
    values: toFieldValues(RECOMMENDATION_TYPES),
  },
  SHOULD_SHOW_IN_LIST: {
    label: PRESENTATION_ATTRIBUTES_LABELS.shouldShowInList,
    name: "attributes.shouldShowInList",
    type: "select",
    values: toFieldValues(PRESENTATION_BOOLEAN),
  },
  SHOULD_SHOW_IN_SEARCH_IN_APPS: {
    label: PRESENTATION_ATTRIBUTES_LABELS.shouldShowInSearchInApps,
    name: "attributes.shouldShowInSearchInApps",
    type: "select",
    values: toFieldValues(PRESENTATION_BOOLEAN),
  },
  SORT_ORDER: {
    label: PRESENTATION_ATTRIBUTES_LABELS.sortOrder,
    name: "attributes.sortOrder",
    required: false,
    type: "number",
  },
  SUPPORTS_EPISODE_DEEPLINKS: {
    label: PRESENTATION_ATTRIBUTES_LABELS.supportsEpisodeDeepLinks,
    name: "attributes.supportsEpisodeDeepLinks",
    type: "select",
    values: toFieldValues(PRESENTATION_BOOLEAN),
  },
};

export const FIELDS_PRESENTATION: Record<string, FieldProps> = {
  CONFIGS: {
    label: "Configurations",
    name: "configs",
    type: "array",
    fields: Object.values(FIELDS_PRESENTATION_CONFIG),
  },
  LABEL: {
    label: "Label",
    name: "label",
    // Keep field-level required validation disabled.
    // Required is enforced in Yup schema, and this prevents
    // stale "Field is required" errors when array items are removed/unmounted.
    required: false,
  },
  TARGETS: {
    label: "Targets",
    name: "targets",
    type: "array",
    fields: Object.values(TARGET_FIELDS),
  },
  PARTNER_PICKS_ASPECT_RATIO: {
    label: "Aspect ratio",
    name: "partnerPicks.aspectRatio",
    required: false,
    pattern: /^\d+:\d+$/,
    patternHint: "W:H",
  },
  PARTNER_PICKS_REPORTING: {
    label: "Reporting",
    name: "partnerPicks.reporting",
    required: false,
    type: "select",
    values: toFieldValues(PARTNER_PICKS_REPORTINGS, false),
  },
  PARTNER_PICKS_TYPE: {
    label: "Type",
    name: "partnerPicks.type",
    required: false,
    type: "select",
    values: toFieldValues(PARTNER_PICKS_TYPES, false),
  },
};

export const FIELDS_ZOOM_CONFIG: Record<string, FieldProps> = {
  ZOOM_FACTOR: {
    label: "Zoom factor",
    name: "zoomFactor",
    required: false,
    type: "number",
    min: 1,
  },
  ZOOM_FACTOR_RATIO: {
    label: "Zoom factor ratio",
    name: "zoomFactorRatio",
    required: false,
    type: "number",
    min: 0.01,
  },
  TARGETS: {
    label: "Targets",
    name: "targets",
    type: "array",
    fields: Object.values(TARGET_FIELDS),
  },
};

export const IMAGE_CONFIGS = {
  PRIMARY_BRANDING_LOGO_SQUARE: {
    accept: ["image/jpeg", "image/png"],
    tooltip:
      "Include the application name but not additional text such as promotions, slogans, taglines, or calls to action. The background must be fully filled with a solid color, gradient, or texture.\nPadding: 21px (~3.8%) on all sides.",
    // eslint-disable-next-line no-magic-numbers
    imageWidth: [558, 2232],
    // eslint-disable-next-line no-magic-numbers
    imageHeight: [558, 2232],
    // eslint-disable-next-line no-magic-numbers
    imageAspectRatio: [1, 1],
    imageSizeMib: 10,
  },
  PARTNER_SOURCE_LOGO_SQUARE: {
    accept: ["image/png"],
    tooltip:
      "Color or monochrome. The background must be fully transparent (no white or colored fill).\nPadding: 9px (10%) on all sides.",
    // eslint-disable-next-line no-magic-numbers
    imageWidth: [90, 360],
    // eslint-disable-next-line no-magic-numbers
    imageHeight: [90, 360],
    // eslint-disable-next-line no-magic-numbers
    imageAspectRatio: [1, 1],
    imageSizeMib: 10,
  },
  PARTNER_SOURCE_LOGO_WIDE: {
    accept: ["image/png"],
    tooltip:
      "Color or monochrome. The background must be fully transparent (no white or colored fill).\nPadding: 30px (20%) on the top/bottom, 33px (~8%) on the left/right.",
    // eslint-disable-next-line no-magic-numbers
    imageWidth: [420, 1680],
    // eslint-disable-next-line no-magic-numbers
    imageHeight: [150, 600],
    // eslint-disable-next-line no-magic-numbers
    imageAspectRatio: [14, 5],
    imageSizeMib: 10,
  },
  PRIMARY_BRANDING_LOGO_RECTANGLE: {
    accept: ["image/jpeg", "image/png"],
    tooltip:
      "Include the application name but not additional text such as promotions, slogans, taglines, or calls to action. The background must be fully filled with a solid color, gradient, texture, or image.\nPadding: 60px (~13%) on the top/bottom, 72px (~9%) on the left/right.",
    // eslint-disable-next-line no-magic-numbers
    imageWidth: [816, 3264],
    // eslint-disable-next-line no-magic-numbers
    imageHeight: [459, 1836],
    // eslint-disable-next-line no-magic-numbers
    imageAspectRatio: [16, 9],
    imageSizeMib: 10,
  },
  ATMOSPHERIC: {
    accept: ["image/png"],
    tooltip:
      "Color or monochrome. The background must be fully transparent (no white or colored fill).\nPadding: 72px (~13%) on the top/bottom, 84px (~9%) on the left/right.",
    // eslint-disable-next-line no-magic-numbers
    imageWidth: [960, 3840],
    // eslint-disable-next-line no-magic-numbers
    imageHeight: [540, 2160],
    // eslint-disable-next-line no-magic-numbers
    imageAspectRatio: [16, 9],
    imageSizeMib: 10,
  },
};

const generateImageFields = (environment: IMAGE_ENVS) => {
  return Object.entries(IMAGE_CONFIGS).reduce(
    (acc, [fieldKey, config]) => {
      const suffix = environment === IMAGE_ENVS.PROD ? "_PROD" : "";
      acc[`${fieldKey}${suffix}`] = {
        name: `assets.${environment}.${IMAGES[fieldKey as keyof typeof IMAGES]}.url`,
        label: IMAGE_LABELS[IMAGES[fieldKey as keyof typeof IMAGES]],
        required: false,
        ...config,
      };
      return acc;
    },
    {} as Record<string, any>
  );
};

export const PROVIDER_FIELDS: FieldsConfig = {
  BASE: {
    APP_LABEL: {
      label: "App label",
      name: "label",
      tooltip:
        "A short name to be used internally in this dashboard to refer to this provider. It will not be visible to end users",
    },
    DISPLAY_NAME: {
      label: "Display name",
      name: "displayName",
      tooltip:
        "This is the name that will be used to represent the App in various UI element. This name will be visible to end users on devices",
    },
    ALLOW_LIST: {
      label: "Allow list",
      name: "allowList",
      required: false,
      type: "array",
      tooltip:
        "List of domains, in form of regular expressions, that are associated with this Provider app or set of apps. This is used in Automotive only, since many Car OEMs don't allow all domains to be opened in their browser hence they need an explicit list",
    },
    CATEGORY: {
      label: "App category",
      name: "appCategories",
      type: "checkbox",
      required: false,
      values: toFieldValues(APP_CATEGORIES),
    },
    PROVIDER_ID: {
      label: "Provider ID",
      name: "providerId",
      pattern: PROVIDER_ID_PATTERN,
      patternHint: 'letters, numbers, _, :, -, ", .',
      tooltip:
        "A unique ID that represents this provider within the TiVo OS ecosystem (both TV and Car). This ID is used e.g. to associate this provider with their catalog (movies, shows) ingested via a different system, or to identify this provider in analytics system. Typically, this is the same as the app name",
    },
    UNIVERSAL_PROVIDER_ID: {
      label: "Universal Provider ID",
      name: "universalProviderId",
      pattern: PROVIDER_ID_PATTERN,
      patternHint: 'letters, numbers, _, :, -, ", .',
      tooltip:
        "A unique ID that represents this provider within the entire TiVo ecosystem (TV, Car and PayTV). Typically, this is the same as the app name and in most cases is the same as the Provider ID above, except when the Provider ID has some legacy naming.",
    },
    JW_PROVIDER_ID: {
      label: "JW Provider ID",
      name: "jwProviderId",
      type: "array",
      tooltip:
        "A unique ID that represents this provider within the Just Watch catalog. Just Watch is a 3rd party providing metadata to TiVo",
    },
  },
  IMAGES: {
    ...generateImageFields(IMAGE_ENVS.STAGING),
    ...generateImageFields(IMAGE_ENVS.PROD),
  },
  COUNTRIES: {
    WORLDWIDE: {
      label: "The app is approved to be distributed:",
      name: "worldwide",
      type: "radio",
      values: [
        { value: "unset", label: "Inherit from provider" },
        { value: "true", label: "Worldwide" },
        { value: "false", label: "In specific countries" },
      ],
    },
    SUPPORTED_COUNTRIES: {
      label: "Supported countries",
      name: "countries",
      required: false,
      type: "array",
    },
    COUNTRIES_INPUT: {
      label: "Target Countries (ISO)",
      placeholder: "Add countries using ISO codes like US, CA, FR",
      name: "countries",
      required: false,
      type: "array",
    },
  },
  SUPPORT: {
    CONTACTS: {
      label: "Additional contacts",
      name: "supportAdditionalContacts",
      required: false,
      type: "textarea",
    },
    EMAIL: {
      label: "Support email",
      name: "supportEmail",
      required: false,
    },
    PHONE: {
      label: "Support phone",
      name: "supportPhone",
      required: false,
      type: "tel",
    },
    PRIORITY: {
      label: "Support priority",
      name: "supportPriority",
      type: "select",
      values: toFieldValues(SUPPORT_PRIORITY),
    },
    NOTES: {
      label: "Support notes",
      name: "supportNotes",
      required: false,
      type: "textarea",
    },
    SLA: {
      label: "Support SLA",
      name: "supportSla",
      required: false,
      type: "textarea",
    },
  },
  CSP_SUMMARY: {
    ASSIGNEE_BDM: {
      label: "Business Owner (BD)",
      name: "assignedBdm",
      required: false,
    },
    ASSIGNEE_TAM: {
      label: "Technical Account Manager (TAM)",
      name: "assignedTam",
      required: false,
    },
    REQUIRE_CONFIRMATION: {
      label: "Requires CSP approval before publication",
      name: "cspConfirmationRequired",
      type: "radio",
      values: toFieldValues(YES_NO_UNKNOWN),
    },
    GROUP: {
      label: "CSP group",
      name: "cspGroup",
      required: false,
      type: "select",
      values: toFieldValues(CSP_GROUP_LABELS, false),
    },
    BIZ_MODEL: {
      label: "CSP business model",
      name: "cspBusinessModel",
      required: true,
      type: "checkbox",
      values: toFieldValues(CSP_BIZ_MODEL_LABELS),
    },
    AGREEMENT_MODEL: {
      label: "Commercial agreement type",
      name: "commercialAgreementType", // revenueModel
      required: false,
      type: "checkbox",
      values: toFieldValues(COMMERCIAL_AGREEMENT_TYPE),
    },
    AGREEMENT_SUMMARY: {
      label: "Commercial agreement summary",
      name: "commercialAgreementSummary",
      required: false,
      type: "textarea",
    },
    ACR_RIGHTS: {
      label: "Do we have ACR rights?",
      name: "acrRights",
      required: false,
      type: "select",
      values: toFieldValues(ACR_RIGHTS_LABELS, false),
    },
    CONTRACT_ORIGIN: {
      label: "Contract origin",
      name: "contractOrigin",
      type: "select",
      values: toFieldValues(CONTRACT_ORIGIN),
    },
    CONTRACT_STATUS: {
      label: "Contract status",
      name: "contractStatus",
      required: false,
      type: "select",
      values: toFieldValues(CONTRACT_STATUS, false),
    },
    REPORTING_OBLIGATIONS: {
      label: "Reporting obligations",
      name: "reportingObligations",
      required: false,
      type: "textarea",
    },
    PROMO_OBLIGATIONS: {
      label: "Promotion obligations",
      name: "promotionsObligations",
      required: false,
      type: "textarea",
    },
    PLACEMENT_OBLIGATIONS: {
      label: "Placement obligations",
      name: "placementObligations",
      required: false,
      type: "textarea",
    },
    DEAL_SUMMARY: {
      label: "Deal summary",
      name: "dealSummary",
      required: false,
      type: "textarea",
    },
  },
  MEDIA: {
    GROUP: {
      label: "",
      name: "media",
      required: false,
      type: "",
    },
  },
  PRESENTATION: {
    RELEASE_STAGE_PRESENTATIONS: {
      label: "",
      name: "presentations",
      type: "",
    },
  },
};

export const VARIANT_FIELDS: FieldsConfig = {
  LAUNCH_CONFIG: {
    ADS_ENABLED: {
      label: "Ads enabled",
      name: "adsEnabled",
      values: [
        { label: trans.YES(), value: "true" },
        { label: trans.NO(), value: "false" },
      ],
      type: "radio",
    },

    APP_TYPE: {
      label: "App type",
      name: "launchAttributes.appType",
      type: "select",
      initOption: {
        value: "",
        text: "",
      },
      values: toFieldValues(APP_TYPE),
    },
    TARGETS: {
      label: "Targets",
      name: "targets",
      fields: Object.values(TARGET_FIELDS),
      type: "array",
    },
    LAUNCH_TAB: {
      label: LAUNCH_TYPE_LABELS.launch,
      name: "launchAttributes.launch",
      required: false,
      type: "",
    },
    LINEAR_TAB: {
      label: LAUNCH_TYPE_LABELS.linear,
      name: "launchAttributes.linear",
      required: false,
      type: "",
    },
    SEARCH_LAUNCH_TAB: {
      label: LAUNCH_TYPE_LABELS.searchLaunch,
      name: "launchAttributes.searchLaunch",
      required: false,
      type: "",
    },
    SERIES_BROWSE_TAB: {
      label: LAUNCH_TYPE_LABELS.seriesBrowse,
      name: "launchAttributes.seriesBrowse",
      required: false,
      type: "",
    },
    VARIANT_LABEL: {
      label: "Variant label",
      name: "label",
    },
    VOD_TAB: {
      label: LAUNCH_TYPE_LABELS.vod,
      name: "launchAttributes.vod",
      required: false,
      type: "",
    },
  },
  URI_TRANSFORM: {
    ALT_PLAYBACK_URI: {
      label: "Alternative playback URI",
      name: "uriTransform.alternateBasePlaybackUrl",
      required: false,
    },
    CLASS_NAME: {
      label: "Class name",
      name: "uriTransform.className",
      required: false,
    },
    ENABLED: {
      label: "Enable URI transform",
      name: "uriTransform.enabled",
      type: "switch",
    },
    NAME_VALUE_MAP: {
      label: "Name value map",
      name: "uriTransform.providerSpecificNameValueMap",
      required: false,
      type: "array",
    },
  },
  ZOOM_CONFIG: {
    CONFIGURATIONS: {
      label: "Zoom configurations",
      name: "zoomConfigurations",
      required: false,
      type: "array",
      fields: Object.values(FIELDS_ZOOM_CONFIG),
    },
  },
};

/*
 * A template for the launch config tab fields.
 */
export const FIELDS_LAUNCH_CONFIG_TAB: Record<
  string,
  FieldProps & { modes: string[] } & Record<string, any>
> = {
  // @todo We could validate the playbackUri format.
  PLAYBACK_URI: {
    label: "Playback URI",
    name: "playbackUri",
    modes: ["uri"],
  },
  AD_PARAMS: {
    label: "Ad parameters template",
    name: "adParamsTemplate",
    modes: ["uri"],
    required: false,
  },
  APPEND_URL_PARAMS: {
    label: "Append URL parameters",
    name: "appendUrlParameters",
    modes: ["uri"],
    required: false,
    type: "array",
    template: {
      key: "",
      name: "",
      placement: "",
      value: "",
    },
  },
  // @todo We could validate the attributesWithCriteria data.
  ATTRS_WITH_CRIT: {
    label: "Attributes with criteria",
    name: "attributesWithCriteria",
    accept: ["application/json"],
    height: "250px",
    type: "json",
    modes: ["intent", "uri"],
    required: false,
  },
  // @todo We could validate the intent data.
  INTENT: {
    label: "Intent",
    name: "intent",
    accept: ["application/json"],
    height: "250px",
    type: "json",
    modes: ["intent"],
  },
};
